import React, { useEffect, useState } from "react";
import { Settings as SettingsIcon, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { useAuth } from "@/context/AuthContext";

const inputCls = "h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200";

function Row({ label, desc, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-50 py-3 last:border-0">
      <div><p className="text-sm font-medium text-slate-700">{label}</p>{desc && <p className="text-xs text-slate-400">{desc}</p>}</div>
      {children}
    </div>
  );
}

export default function ClientSettings() {
  const { user } = useAuth();
  const readOnly = !["client_admin"].includes(user?.role) && !user?.permissions?.manageSettings;
  const [s, setS] = useState(null);
  const [client, setClient] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then(({ data }) => { setS(data.settings); setClient(data.client); }); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        businessInfo: s.businessInfo, aiSettings: s.aiSettings, reviewSettings: s.reviewSettings,
        notificationSettings: s.notificationSettings, whatsappSettings: s.whatsappSettings,
        postSettings: s.postSettings, photoSettings: s.photoSettings,
        client: { reviewLink: s.businessInfo?.reviewLink },
      });
      toast.success("Settings saved");
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  if (!s) return <div className="space-y-6"><PageHeader title="Settings" /></div>;
  const upd = (section, key, value) => setS((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" subtitle="Business profile, AI behaviour and automation preferences" icon={SettingsIcon}
        actions={!readOnly && <Button onClick={save} disabled={saving} className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" data-testid="save-settings-btn"><Save className="mr-1 h-4 w-4" /> {saving ? "Saving..." : "Save"}</Button>} />

      <Tabs defaultValue="business">
        <TabsList className="rounded-xl">
          <TabsTrigger value="business">Business</TabsTrigger>
          <TabsTrigger value="ai">AI & Replies</TabsTrigger>
          <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="business" className="mt-4">
          <div className="card-surface space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Website</label><input className={inputCls} value={s.businessInfo?.website || ""} onChange={(e) => upd("businessInfo", "website", e.target.value)} /></div>
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Phone</label><input className={inputCls} value={s.businessInfo?.phone || ""} onChange={(e) => upd("businessInfo", "phone", e.target.value)} /></div>
              <div><label className="mb-1 block text-xs font-medium text-slate-600">WhatsApp Number</label><input className={inputCls} value={s.businessInfo?.whatsapp || ""} onChange={(e) => upd("businessInfo", "whatsapp", e.target.value)} /></div>
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Email</label><input className={inputCls} value={s.businessInfo?.email || ""} onChange={(e) => upd("businessInfo", "email", e.target.value)} /></div>
              <div className="sm:col-span-2"><label className="mb-1 block text-xs font-medium text-slate-600">Google Review Link</label><input className={inputCls} value={s.businessInfo?.reviewLink || ""} onChange={(e) => upd("businessInfo", "reviewLink", e.target.value)} placeholder="https://g.page/r/..." data-testid="review-link-input" /></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ai" className="mt-4">
          <div className="card-surface p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Default Tone</label>
                <Select value={s.aiSettings?.tone} onValueChange={(v) => upd("aiSettings", "tone", v)}><SelectTrigger className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{["professional", "friendly", "short", "detailed", "apology"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Default Language</label>
                <Select value={s.aiSettings?.language} onValueChange={(v) => upd("aiSettings", "language", v)}><SelectTrigger className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{["english", "hinglish", "hindi"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="sm:col-span-2"><label className="mb-1 block text-xs font-medium text-slate-600">Reply Signature</label><input className={inputCls} value={s.aiSettings?.signature || ""} onChange={(e) => upd("aiSettings", "signature", e.target.value)} /></div>
            </div>
            <h4 className="mt-6 mb-2 font-heading text-sm font-semibold text-slate-700">Auto Reply Rules</h4>
            <Row label="Auto-generate replies" desc="Generate AI replies automatically for new reviews"><Switch checked={s.reviewSettings?.autoGenerateReply} onCheckedChange={(v) => upd("reviewSettings", "autoGenerateReply", v)} /></Row>
            <Row label="Auto-publish 5-star replies"><Switch checked={s.reviewSettings?.autoPublish5Star} onCheckedChange={(v) => upd("reviewSettings", "autoPublish5Star", v)} /></Row>
            <Row label="Auto-publish 4-star replies"><Switch checked={s.reviewSettings?.autoPublish4Star} onCheckedChange={(v) => upd("reviewSettings", "autoPublish4Star", v)} /></Row>
            <Row label="Require approval for 1–3 star" desc="Always on for safety — negative reviews never auto-reply"><Switch checked disabled /></Row>
          </div>
        </TabsContent>

        <TabsContent value="whatsapp" className="mt-4">
          <div className="card-surface space-y-4 p-6">
            <Row label="Enable WhatsApp" desc="Use WhatsApp Cloud API for review requests & alerts"><Switch checked={s.whatsappSettings?.enabled} onCheckedChange={(v) => upd("whatsappSettings", "enabled", v)} /></Row>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Business Account ID</label><input className={inputCls} value={s.whatsappSettings?.businessAccountId || ""} onChange={(e) => upd("whatsappSettings", "businessAccountId", e.target.value)} /></div>
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Phone Number ID</label><input className={inputCls} value={s.whatsappSettings?.phoneNumberId || ""} onChange={(e) => upd("whatsappSettings", "phoneNumberId", e.target.value)} /></div>
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Access Token</label><input type="password" className={inputCls} value={s.whatsappSettings?.accessToken || ""} onChange={(e) => upd("whatsappSettings", "accessToken", e.target.value)} /></div>
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Test Number</label><input className={inputCls} value={s.whatsappSettings?.testNumber || ""} onChange={(e) => upd("whatsappSettings", "testNumber", e.target.value)} /></div>
            </div>
            <p className="text-xs text-amber-600">Server-level WhatsApp keys must also be configured to send live messages.</p>
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <div className="card-surface p-6">
            <Row label="Email notifications"><Switch checked={s.notificationSettings?.email} onCheckedChange={(v) => upd("notificationSettings", "email", v)} /></Row>
            <Row label="WhatsApp notifications"><Switch checked={s.notificationSettings?.whatsapp} onCheckedChange={(v) => upd("notificationSettings", "whatsapp", v)} /></Row>
            <Row label="Bad review alerts" desc="Get notified instantly for 1–2 star reviews"><Switch checked={s.notificationSettings?.badReviewAlert} onCheckedChange={(v) => upd("notificationSettings", "badReviewAlert", v)} /></Row>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
